import { apiClient, web3APIClient } from "@/api/api.ts";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

export type Token = {
  contract_address: string;
  created_at: string;
  decimals: number;
  is_locked: boolean;
  is_pool_created: boolean;
  name: string;
  persona_id: string;
  image_url?: string; // TODO once we retrofit existing tokens, this will be required, not optional
  pool?: {
    created_at: string;
    initial_liquidity_sol: number;
    initial_liquidity_token: number;
    lp_mint: string;
    observation_id: string;
    pool_authority: string;
    pool_config_id: string;
    pool_id: string;
    token_vault_a: string;
    token_vault_b: string;
  };
  symbol: string;
  total_supply: number;
};

export interface TokenMetrics {
  price: {
    price?: number;
    variation24h?: number;
    volume24h?: number;
    sells24h?: number;
    buys24h?: number;
  };
  marketMetrics: {
    marketCap?: number;
    fdv?: number;
    holders?: string;
    liquidity?: number;
    totalSupply?: number;
  };
}

/**
 * Fetch the full token price payload from Dextools with Basic Auth.
 * @param contractAddress The contract address of the token.
 * @param blockchain The blockchain of the token (e.g., 'ethereum', 'solana').
 * @returns The full payload containing the token price and other details.
 */
export const fetchTokenPrice = async (
  contractAddress: string,
  pool: string,
  blockchain: string,
): Promise<TokenMetrics> => {
  const response = await web3APIClient.get(`/dextools/token-metrics`, {
    params: {
      address: contractAddress,
      blockchain: blockchain,
      pool: pool,
    },
  });
  return response.data;
};

export function useTokenPriceQuery(
  contractAddress: string,
  pool: string,
  blockchain: string,
  options?: Pick<UseQueryOptions<TokenMetrics, Error>, "select" | "enabled">,
) {
  return useQuery({
    queryKey: ["tokenPrice", contractAddress, pool, blockchain],
    queryFn: () => fetchTokenPrice(contractAddress, pool, blockchain),
    ...options,
  });
}

const fetchGraduatingTokenData = async (
  persona_id: string,
  contract_address: string,
) => {
  try {
    if (contract_address === "") {
      return null;
    }
    const url = `/api/tokens/${persona_id}/token-pool/${contract_address}`;
    const response = await apiClient.get<any>(url);

    return response.data;
  } catch (error) {
    console.error("Error fetching graduatingtoken:", error);
    throw error;
  }
};

export function useGraduatingTokenQuery<Token>(
  persona_id: string,
  contract_address: string,
  options?: Pick<UseQueryOptions<Token, Error>, "select" | "enabled">,
) {
  return useQuery({
    ...options,
    queryKey: ["token", persona_id, contract_address],
    queryFn: () => fetchGraduatingTokenData(persona_id, contract_address),
  });
}

const fetchGraduatingTokensData = async () => {
  try {
    const url = `/api/tokens/graduating`;
    const response = await apiClient.get(url);
    // Convert the dictionary of personas to an array
    return response.data;
  } catch (error) {
    console.error("Error fetching graduatingtokens:", error);
    throw error;
  }
};

export function useGraduatingTokensQuery<Token>(
  options?: Pick<UseQueryOptions<Token, Error>, "select">,
) {
  return useQuery({
    ...options,
    queryKey: ["tokens"],
    queryFn: () => fetchGraduatingTokensData(),
  });
}

export const uploadTokenImage = async (persona_id: string, file: any) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiClient.post(
      `/api/tokens/${persona_id}/upload-token-image`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error uploading token image:", error);
    throw error;
  }
};
// States

export const tokenQueries = {
  tokenPrice: ({
    contractAddress,
    pool,
    blockchain,
  }: {
    contractAddress: string;
    pool: string;
    blockchain: string;
  }) => ({
    queryKey: ["token", { contractAddress, pool, blockchain }, "price"],
    queryFn: () => fetchTokenPrice(contractAddress, pool, blockchain),
  }),
};
